import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, Bot, Calendar, MapPin, Search, 
  ArrowRight, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const services = [
  { text: 'Smart website design', icon: Globe },
  { text: 'AI chatbots & automation', icon: Bot },
  { text: 'Booking systems', icon: Calendar },
  { text: 'Google Business optimization', icon: MapPin },
  { text: 'Local SEO strategy', icon: Search },
];

const ServicesDigitalPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const digitalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Fearless IT Digital Services",
    "serviceType": "Digital Marketing and Web Development",
    "description": "Smart websites, AI chatbots, booking systems, Google Business optimization, and local SEO strategy for Las Vegas businesses.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Fearless IT",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Las Vegas",
        "addressRegion": "NV",
        "addressCountry": "US"
      },
      "telephone": "(702) 727-1773",
      "url": "https://fearlessit.io"
    },
    "areaServed": {
      "@type": "City",
      "name": "Las Vegas"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Digital Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Smart Website Design" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Chatbots & Automation" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Booking Systems" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Google Business Optimization" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Local SEO Strategy" }}
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="services-digital-page">
      <SEO 
        title="Digital Services - Smart Websites, Applications & Growth Systems"
        description="Fearless IT Digital: We design modern digital platforms that help Las Vegas businesses attract customers and operate efficiently. Smart website design, AI chatbots, booking systems, Google Business optimization, and local SEO strategy."
        keywords="website design Las Vegas, AI chatbots Las Vegas, booking systems, Google Business optimization Las Vegas, local SEO Las Vegas, digital marketing Nevada, web development Las Vegas, business automation"
        url="/services/digital"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/0fdefd8468f275a4970395292fb2f445b60b822fa9581213de045d69963c4019.png"
        serviceName="Digital Marketing and Web Development"
        structuredData={digitalServiceSchema}
        isLocalPage={true}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
            <Globe className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Digital Services</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="digital-title">
            Fearless IT Digital
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #0066FF, #5B3DF5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Smart Websites, Applications & Growth Systems
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            We design modern digital platforms that help businesses attract customers and operate efficiently.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-sm font-semibold text-slate-gray uppercase tracking-wider mb-8 text-center">
            Services Include
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {services.map((service, idx) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={idx} 
                  className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-lg font-medium text-carbon-black">{service.text}</span>
                </div>
              );
            })}
          </div>
          
          {/* Closing Text */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 text-center border border-blue-100">
            <CheckCircle2 className="h-10 w-10 text-blue-600 mx-auto mb-4" />
            <p className="text-lg sm:text-xl text-carbon-black font-medium">
              Perfect for businesses that want a stronger online presence and more customers.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Ready to grow your digital presence?
          </h2>
          <p className="text-slate-gray mb-8 max-w-xl mx-auto">
            Let's discuss how we can help your business attract more customers online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/growth-audit">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-slate-gray text-slate-gray hover:border-blue-500 hover:text-blue-500 transition-all">
                Free Growth Audit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ServicesDigitalPage;
